import { useState, useRef, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useNavigate } from 'react-router-dom';
import { Search, Building2, Users, Radio, X, Megaphone, BookText, FileUp, Video } from 'lucide-react';
import { Input } from '@/components/ui/input';

interface SearchResult {
  id: string;
  type: 'brand' | 'affiliate' | 'session' | 'campaign' | 'sop' | 'upload' | 'content';
  title: string;
  subtitle: string;
  navigateTo: string;
}

export function GlobalSearch() {
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const inputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  useEffect(() => {
    // ⌘K is now owned by the global CommandPalette. GlobalSearch keeps only
    // the Escape-to-close behavior for its inline dropdown.
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setOpen(false);
        inputRef.current?.blur();
      }
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, []);

  const { data: results } = useQuery({
    queryKey: ['global-search', query],
    queryFn: async (): Promise<SearchResult[]> => {
      if (query.length < 2) return [];
      const safe = query.trim().replace(/[(),]/g, ' ').replace(/[%_]/g, '\\$&');
      const q = `%${safe}%`;
      const [brandsRes, affiliatesRes, sessionsRes, campaignsRes, sopsRes, uploadsRes, contentRes] = await Promise.all([
        supabase.from('brands').select('id, name, category, status').ilike('name', q).limit(5),
        supabase.from('affiliates').select('id, tiktok_handle, full_name, brand_id').or(`tiktok_handle.ilike.${q},full_name.ilike.${q}`).limit(5),
        supabase.from('live_sessions').select('id, stream_title, brand_id, brands(name)').ilike('stream_title', q).limit(5),
        supabase.from('campaigns').select('id, name, brand_id').ilike('name', q).limit(5),
        supabase.from('agency_sops').select('id, title, category').ilike('title', q).limit(5),
        supabase.from('tiktok_shop_uploads').select('id, file_name, brand_id, report_type').ilike('file_name', q).limit(5),
        (supabase as any).from('campaign_content').select('id, title, campaign_id, tiktok_handle, tiktok_url').or(`title.ilike.${q},tiktok_url.ilike.${q},tiktok_handle.ilike.${q}`).limit(5),
      ]);
      const results: SearchResult[] = [];
      brandsRes.data?.forEach(b => results.push({ id: b.id, type: 'brand', title: b.name, subtitle: `${b.category || 'Brand'} · ${b.status}`, navigateTo: `/brands/${b.id}` }));
      affiliatesRes.data?.forEach(a => results.push({ id: a.id, type: 'affiliate', title: `@${a.tiktok_handle}`, subtitle: a.full_name || 'Affiliate', navigateTo: `/brands/${a.brand_id}/affiliates` }));
      sessionsRes.data?.forEach(s => results.push({ id: s.id, type: 'session', title: s.stream_title || 'Untitled Session', subtitle: (s as any).brands?.name || 'Session', navigateTo: `/brands/${s.brand_id}/live` }));
      campaignsRes.data?.forEach((c: any) => results.push({ id: c.id, type: 'campaign', title: c.name, subtitle: 'Campaign', navigateTo: `/campaigns/${c.id}` }));
      sopsRes.data?.forEach((s: any) => results.push({ id: s.id, type: 'sop', title: s.title, subtitle: `SOP · ${s.category}`, navigateTo: `/settings/agency` }));
      uploadsRes.data?.forEach((u: any) => results.push({ id: u.id, type: 'upload', title: u.file_name, subtitle: `Upload · ${u.report_type}`, navigateTo: `/brands/${u.brand_id}` }));
      contentRes.data?.forEach((v: any) => results.push({ id: v.id, type: 'content', title: v.title || 'Untitled video', subtitle: `Video${v.tiktok_handle ? ` · @${v.tiktok_handle}` : ''}`, navigateTo: `/campaigns/${v.campaign_id}` }));
      return results;
    },
    enabled: query.length >= 2,
  });

  const iconMap = { brand: Building2, affiliate: Users, session: Radio, campaign: Megaphone, sop: BookText, upload: FileUp, content: Video };

  const handleSelect = (result: SearchResult) => {
    navigate(result.navigateTo);
    setQuery('');
    setOpen(false);
  };

  return (
    <div ref={containerRef} className="relative">
      <div className="relative">
        <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-white/50" />
        <Input
          ref={inputRef}
          value={query}
          onChange={e => { setQuery(e.target.value); setOpen(true); }}
          onFocus={() => query.length >= 2 && setOpen(true)}
          placeholder="Search FlowTok OS… ⌘K"
          className="ftos-search-input h-9 w-48 sm:w-72 pl-8 pr-8 rounded-lg"
          style={{ fontSize: '13px' }}
        />
        {query && (
          <button onClick={() => { setQuery(''); setOpen(false); }} className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground">
            <X className="h-3 w-3" />
          </button>
        )}
      </div>

      {open && results && results.length > 0 && (
        <div className="absolute top-full mt-2 left-0 right-0 sm:w-80 bg-card border border-border rounded-xl shadow-2xl z-50 overflow-hidden animate-fade-in">
          <div className="p-2 space-y-0.5 max-h-80 overflow-y-auto">
            {results.map(r => {
              const Icon = iconMap[r.type];
              return (
                <button
                  key={`${r.type}-${r.id}`}
                  onClick={() => handleSelect(r)}
                  className="w-full flex items-center gap-3 p-2.5 rounded-lg hover:bg-secondary/80 transition-colors text-left"
                >
                  <div className="h-8 w-8 rounded-lg bg-secondary flex items-center justify-center shrink-0">
                    <Icon className="h-3.5 w-3.5 text-primary" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium text-foreground truncate">{r.title}</p>
                    <p className="text-xs text-muted-foreground truncate">{r.subtitle}</p>
                  </div>
                  <span className="label-style text-muted-foreground/50 shrink-0">{r.type}</span>
                </button>
              );
            })}
          </div>
        </div>
      )}

      {open && query.length >= 2 && results?.length === 0 && (
        <div className="absolute top-full mt-2 left-0 right-0 sm:w-80 bg-card border border-border rounded-xl shadow-2xl z-50 p-6 text-center animate-fade-in">
          <p className="text-sm text-muted-foreground">No results for "{query}"</p>
        </div>
      )}
    </div>
  );
}
